import React, { useEffect, useState } from "react";
import { Plus, Trash2, Users, KeyRound, Copy, Check, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/Page";

const ROLES = [
  { value: "brand_admin", label: "Brand Admin", desc: "Manage assigned brands fully — content, ads, CRM. No user management." },
  { value: "marketer", label: "Marketer", desc: "Create content + run campaigns on assigned brands. No CRM access." },
  { value: "crm_user", label: "CRM User", desc: "Only CRM tab — leads, kanban, notes, tasks." },
  { value: "viewer", label: "Viewer", desc: "Read-only across CRM + Calendar. Cannot edit anything." },
];

const roleLabel = (v) => ROLES.find((r) => r.value === v)?.label || v;

const Team = () => {
  const [members, setMembers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "crm_user", assigned_brand_ids: [] });
  const [saving, setSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, b] = await Promise.all([
        api.get("/team/members"),
        api.get("/brands"),
      ]);
      setMembers(m.data.members || []);
      setBrands(b.data.brands || []);
    } catch (e) {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleBrand = (id) => {
    setForm((f) => ({
      ...f,
      assigned_brand_ids: f.assigned_brand_ids.includes(id)
        ? f.assigned_brand_ids.filter((x) => x !== id)
        : [...f.assigned_brand_ids, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (form.assigned_brand_ids.length === 0) {
      toast.error("Assign at least one brand");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/team/invite", {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        assigned_brand_ids: form.assigned_brand_ids,
        password: form.password || undefined,
      });
      setInviteResult({ email: data.member.email, password: data.initial_password, role: data.member.role });
      setForm({ name: "", email: "", password: "", role: "crm_user", assigned_brand_ids: [] });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally {
      setSaving(false);
    }
  };

  const copyCreds = async () => {
    if (!inviteResult) return;
    const txt = `Login: ${inviteResult.email}\nPassword: ${inviteResult.password}\nURL: ${window.location.origin}/login`;
    try {
      await navigator.clipboard.writeText(txt);
      setCopied(true);
      toast.success("Credentials copied!");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const toggleActive = async (m) => {
    try {
      await api.patch(`/team/members/${m.id}`, { is_active: !m.is_active });
      toast.success(m.is_active ? "Account deactivated" : "Account reactivated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const resetPassword = async (m) => {
    const pw = prompt(`Set a new password for ${m.email} (min 6 chars):`);
    if (!pw || pw.length < 6) return;
    try {
      await api.patch(`/team/members/${m.id}`, { reset_password: pw });
      toast.success(`Password reset for ${m.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const removeMember = async (m) => {
    if (!confirm(`Permanently remove ${m.email} from your team?`)) return;
    try {
      await api.delete(`/team/members/${m.id}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  const reassignBrands = async (m) => {
    const current = new Set(m.assigned_brand_ids || []);
    const picks = prompt(
      `Brands assigned to ${m.email}:\n\n${brands.map((b, i) => `${i + 1}. [${current.has(b.id) ? "x" : " "}] ${b.name}`).join("\n")}\n\nType comma-separated numbers (e.g., 1,3):`,
      Array.from(current).map((id) => brands.findIndex((b) => b.id === id) + 1).filter((n) => n > 0).join(",")
    );
    if (picks === null) return;
    const nums = picks.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => n > 0 && n <= brands.length);
    const ids = [...new Set(nums.map((n) => brands[n - 1].id))];
    try {
      await api.patch(`/team/members/${m.id}`, { assigned_brand_ids: ids });
      toast.success("Brand access updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Users"
        subtitle="Invite team members and assign them roles + brand-scoped access. Each user only sees what their role permits."
        testId="users-header"
        actions={
          <button onClick={() => { setInviteResult(null); setShowModal(true); }} disabled={brands.length === 0} className="op-btn-primary disabled:opacity-50" data-testid="team-invite-btn">
            <Plus size={16} /> Invite User
          </button>
        }
      />

      {brands.length === 0 && (
        <div className="op-card p-4 mb-4 bg-amber-50 border-amber-200">
          <p className="text-sm text-amber-900">⚠ Create at least one Brand before inviting team members.</p>
        </div>
      )}

      {loading ? (
        <div className="op-card p-10 text-center text-gray-500">Loading team…</div>
      ) : members.length === 0 ? (
        <div className="op-card">
          <EmptyState
            icon={Users}
            title="No users yet"
            description="Invite team members and assign each a role + the brands they can access."
            action={brands.length > 0 ? <button onClick={() => setShowModal(true)} className="op-btn-primary"><Plus size={16} /> Invite User</button> : null}
          />
        </div>
      ) : (
        <div className="op-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs font-semibold text-gray-600 uppercase">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Brand Access</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const brandNames = (m.assigned_brand_ids || []).map((id) => brands.find((b) => b.id === id)?.name).filter(Boolean);
                return (
                  <tr key={m.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`team-row-${m.email}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{m.name}</td>
                    <td className="px-4 py-3 text-gray-600">{m.email}</td>
                    <td className="px-4 py-3">
                      <select
                        value={m.role}
                        onChange={async (e) => {
                          try {
                            await api.patch(`/team/members/${m.id}`, { role: e.target.value });
                            toast.success("Role updated");
                            await load();
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Role update failed");
                          }
                        }}
                        className="text-xs px-2 py-1 bg-purple-50 text-[#7C3AED] rounded font-medium border-0 outline-none cursor-pointer"
                        data-testid={`team-role-${m.email}`}
                      >
                        {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{brandNames.length ? brandNames.join(", ") : <span className="text-gray-400">No brands</span>}</td>
                    <td className="px-4 py-3">{m.is_active === false ? <span className="text-red-600 text-xs">Inactive</span> : <span className="text-emerald-600 text-xs">Active</span>}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => reassignBrands(m)} title="Edit brand access" className="p-1.5 text-gray-500 hover:text-[#7C3AED] hover:bg-gray-50 rounded">
                          <Users size={14} />
                        </button>
                        <button onClick={() => resetPassword(m)} title="Reset password" className="p-1.5 text-gray-500 hover:text-[#7C3AED] hover:bg-gray-50 rounded">
                          <KeyRound size={14} />
                        </button>
                        <button onClick={() => toggleActive(m)} title={m.is_active === false ? "Reactivate" : "Deactivate"} className="p-1.5 text-gray-500 hover:text-[#7C3AED] hover:bg-gray-50 rounded">
                          {m.is_active === false ? <ShieldCheck size={14} /> : <ShieldOff size={14} />}
                        </button>
                        <button onClick={() => removeMember(m)} title="Remove" className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-gray-50 rounded">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            {inviteResult ? (
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2">✅ User invited</h2>
                <p className="text-sm text-gray-600 mb-4">Share these credentials with <strong>{roleLabel(inviteResult.role)}</strong>. They'll only see what their role permits.</p>
                <div className="bg-gray-50 rounded-lg p-4 text-sm font-mono break-all space-y-1">
                  <div><span className="text-gray-500">Email:</span> {inviteResult.email}</div>
                  <div><span className="text-gray-500">Password:</span> {inviteResult.password}</div>
                  <div><span className="text-gray-500">URL:</span> {window.location.origin}/login</div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={copyCreds} className="op-btn-secondary flex-1">
                    {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied!" : "Copy all"}
                  </button>
                  <button onClick={() => { setShowModal(false); setInviteResult(null); }} className="op-btn-primary flex-1">Done</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Invite User</h2>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input className="op-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bob Sales" data-testid="invite-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input type="email" className="op-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="bob@team.com" data-testid="invite-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                    <select className="op-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role">
                      {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">{ROLES.find((r) => r.value === form.role)?.desc}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Initial password <span className="text-gray-400">(blank = auto-generate)</span></label>
                    <input className="op-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="auto-generated" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assigned brands *</label>
                    <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-200 rounded p-2">
                      {brands.map((b) => (
                        <label key={b.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                          <input type="checkbox" checked={form.assigned_brand_ids.includes(b.id)} onChange={() => toggleBrand(b.id)} />
                          <span className="text-sm text-gray-900">{b.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-5">
                  <button type="button" onClick={() => setShowModal(false)} className="op-btn-secondary flex-1">Cancel</button>
                  <button type="submit" disabled={saving} className="op-btn-primary flex-1" data-testid="invite-submit">
                    {saving ? "Inviting…" : "Send Invite"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default Team;
